import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { UtensilsCrossed, BedDouble, Check, Plus, Trash2, ArrowRight, ArrowLeft, MessageCircle, Globe } from "lucide-react";

const SLOT_TYPES = [
  { id: "ristorante", label: "Ristorante" },
  { id: "pizzeria", label: "Pizzeria" },
  { id: "esperienza", label: "Esperienza / Tour" },
];
const NIGHT_TYPES = [
  { id: "bb", label: "B&B" },
  { id: "agriturismo", label: "Agriturismo" },
  { id: "hotel", label: "Hotel" },
];

export default function Onboarding() {
  const { setBusiness } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", type: "ristorante", mode: "slot", phone: "", address: "", description: "",
    resources: [{ name: "Tavolo 1", capacity: 2 }, { name: "Tavolo 2", capacity: 4 }],
    services: [{ name: "Pranzo", start: "12:00", end: "14:30" }, { name: "Cena", start: "19:00", end: "22:30" }],
    slot_interval: 30, turn_duration: 90, min_guests: 1, max_guests: 12,
    min_nights: 1, checkin_time: "15:00", checkout_time: "10:00",
    auto_confirm: false, whatsapp_enabled: true, email_enabled: true,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const pickMode = (mode, type) => set("mode", mode) || setF((p) => ({
    ...p, mode, type,
    resources: mode === "slot"
      ? [{ name: "Tavolo 1", capacity: 2 }, { name: "Tavolo 2", capacity: 4 }]
      : [{ name: "Camera 1", capacity: 2 }, { name: "Camera 2", capacity: 3 }],
  }));

  const addResource = () => setF((p) => ({ ...p, resources: [...p.resources, { name: `${p.mode === "slot" ? "Tavolo" : "Camera"} ${p.resources.length + 1}`, capacity: 2 }] }));
  const rmResource = (i) => setF((p) => ({ ...p, resources: p.resources.filter((_, idx) => idx !== i) }));
  const setResource = (i, k, v) => setF((p) => ({ ...p, resources: p.resources.map((r, idx) => idx === i ? { ...r, [k]: k === "capacity" ? Number(v) : v } : r) }));

  const finish = async () => {
    if (!f.name) { toast.error("Inserisci il nome dell'attività"); setStep(1); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/business", f);
      setBusiness(data);
      toast.success("Attività configurata! 🎉");
      nav("/app");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const steps = ["Attività", "Risorse", "Notifiche", "Portale"];
  const isSlot = f.mode === "slot";

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white"><UtensilsCrossed className="h-5 w-5" /></div>
          <div><p className="font-heading font-bold text-slate-900">Configurazione guidata</p><p className="text-xs text-primary">Powered by turismo.latina.it</p></div>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-primary text-white" : "bg-muted text-slate-400"}`}>
                {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < steps.length - 1 && <div className={`h-1 flex-1 mx-1 rounded ${step > i + 1 ? "bg-emerald-500" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          {step === 1 && (
            <div className="space-y-5" data-testid="onboarding-step-1">
              <h2 className="font-heading text-xl font-bold text-slate-900">Che tipo di attività gestisci?</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className={`rounded-xl border-2 p-4 cursor-pointer ${isSlot ? "border-primary bg-primary/5" : "border-slate-200"}`} onClick={() => pickMode("slot", "ristorante")} data-testid="onboarding-mode-slot">
                  <UtensilsCrossed className="h-6 w-6 text-primary mb-2" />
                  <p className="font-semibold text-slate-900">Tavoli / Coperti</p>
                  <p className="text-xs text-slate-500">Ristoranti, pizzerie, esperienze — prenotazioni a fasce orarie.</p>
                </div>
                <div className={`rounded-xl border-2 p-4 cursor-pointer ${!isSlot ? "border-primary bg-primary/5" : "border-slate-200"}`} onClick={() => pickMode("nights", "bb")} data-testid="onboarding-mode-nights">
                  <BedDouble className="h-6 w-6 text-primary mb-2" />
                  <p className="font-semibold text-slate-900">Camere / Notti</p>
                  <p className="text-xs text-slate-500">B&B, agriturismi, hotel — disponibilità per data.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(isSlot ? SLOT_TYPES : NIGHT_TYPES).map((t) => (
                  <button key={t.id} onClick={() => set("type", t.id)} data-testid={`onboarding-type-${t.id}`}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium border ${f.type === t.id ? "bg-primary text-white border-primary" : "border-slate-300 text-slate-600"}`}>{t.label}</button>
                ))}
              </div>
              <div><Label>Nome attività</Label><Input data-testid="onboarding-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Trattoria del Mare" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Telefono</Label><Input data-testid="onboarding-phone-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+39 0773..." /></div>
                <div><Label>Indirizzo</Label><Input data-testid="onboarding-address-input" value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Via del Porto, Gaeta" /></div>
              </div>
              <div><Label>Descrizione</Label><Textarea data-testid="onboarding-desc-input" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Breve descrizione per la pagina pubblica" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-2">
              <h2 className="font-heading text-xl font-bold text-slate-900">{isSlot ? "Tavoli e fasce orarie" : "Camere e regole soggiorno"}</h2>
              <div className="space-y-2">
                <Label>{isSlot ? "Tavoli" : "Camere"}</Label>
                {f.resources.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={r.name} onChange={(e) => setResource(i, "name", e.target.value)} className="flex-1" data-testid={`onboarding-resource-name-${i}`} />
                    <Input type="number" min={1} value={r.capacity} onChange={(e) => setResource(i, "capacity", e.target.value)} className="w-24" data-testid={`onboarding-resource-cap-${i}`} />
                    <button onClick={() => rmResource(i)} className="p-2 text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addResource} className="rounded-lg" data-testid="onboarding-add-resource"><Plus className="h-4 w-4 mr-1" /> Aggiungi {isSlot ? "tavolo" : "camera"}</Button>
              </div>

              {isSlot ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Min coperti</Label><Input type="number" value={f.min_guests} onChange={(e) => set("min_guests", Number(e.target.value))} /></div>
                    <div><Label>Max coperti</Label><Input type="number" value={f.max_guests} onChange={(e) => set("max_guests", Number(e.target.value))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Intervallo slot (min)</Label><Input type="number" value={f.slot_interval} onChange={(e) => set("slot_interval", Number(e.target.value))} /></div>
                    <div><Label>Durata tavolo (min)</Label><Input type="number" value={f.turn_duration} onChange={(e) => set("turn_duration", Number(e.target.value))} /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Fasce di servizio</Label>
                    {f.services.map((s, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2">
                        <Input value={s.name} onChange={(e) => setF((p) => ({ ...p, services: p.services.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) }))} />
                        <Input type="time" value={s.start} onChange={(e) => setF((p) => ({ ...p, services: p.services.map((x, idx) => idx === i ? { ...x, start: e.target.value } : x) }))} />
                        <Input type="time" value={s.end} onChange={(e) => setF((p) => ({ ...p, services: p.services.map((x, idx) => idx === i ? { ...x, end: e.target.value } : x) }))} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Min notti</Label><Input type="number" value={f.min_nights} onChange={(e) => set("min_nights", Number(e.target.value))} /></div>
                  <div><Label>Check-in</Label><Input type="time" value={f.checkin_time} onChange={(e) => set("checkin_time", e.target.value)} /></div>
                  <div><Label>Check-out</Label><Input type="time" value={f.checkout_time} onChange={(e) => set("checkout_time", e.target.value)} /></div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="onboarding-step-3">
              <h2 className="font-heading text-xl font-bold text-slate-900">Notifiche automatiche</h2>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <MessageCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">WhatsApp (Twilio)</p>
                    <Switch checked={f.whatsapp_enabled} onCheckedChange={(v) => set("whatsapp_enabled", v)} data-testid="onboarding-whatsapp-switch" />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">Conferme e promemoria via numero unico del circuito <b>turismo.latina.it</b>. Collegheremo Twilio con le tue credenziali (ora in modalità simulata).</p>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4 flex items-start gap-3">
                <Globe className="h-5 w-5 text-slate-500 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-900">Email di backup</p>
                    <Switch checked={f.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="onboarding-email-switch" />
                  </div>
                  <p className="text-xs text-slate-600 mt-1">Inviata quando WhatsApp non è disponibile.</p>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                <div><p className="font-semibold text-slate-900">Auto-conferma prenotazioni</p><p className="text-xs text-slate-600">Conferma automatica se ci sono posti disponibili.</p></div>
                <Switch checked={f.auto_confirm} onCheckedChange={(v) => set("auto_confirm", v)} data-testid="onboarding-autoconfirm-switch" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="onboarding-step-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Collegamento al portale</h2>
              <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
                <p className="text-sm text-slate-700">La tua attività verrà collegata a <b>turismo.latina.it</b>. I turisti che cliccano "Prenota" sul portale arriveranno direttamente nella tua dashboard come <b>Lead</b>, con notifica WhatsApp automatica.</p>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5" /> Pagina pubblica di prenotazione con link e QR</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5" /> Lead dal portale instradati in dashboard</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5" /> Conferme e promemoria WhatsApp automatici</li>
              </ul>
              <p className="text-xs text-slate-500">Potrai trovare link pubblico, QR e chiave portale in <b>Impostazioni</b>.</p>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="onboarding-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep((s) => s + 1)} className="rounded-xl" data-testid="onboarding-next-btn">Avanti <ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button onClick={finish} disabled={saving} className="rounded-xl" data-testid="onboarding-finish-btn">{saving ? "Salvataggio..." : "Completa configurazione"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
